import { Component, Input, OnChanges } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { DialogService } from 'src/app/services/dialog.service';
import { ReportService } from 'src/app/services/report.service';
import { faEdit, faFileLines, faTrash, faUsers, faWarning } from '@fortawesome/free-solid-svg-icons';
import { RequestSupportComponent } from '../request-support/request-support.component';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { DeleteFireComponent } from "../../fire-tracker/delete-fire/delete-fire.component";
import { ConfirmationToastComponent } from "../../../components/confirmation-toast/confirmation-toast.component";

@Component({
  selector: 'app-report-list',
  templateUrl: './report-list.component.html',
  styleUrls: ['./report-list.component.scss'],
  standalone: false
})


export class ReportListComponent implements OnChanges {
  faEdit = faEdit;
  faUsers = faUsers;
  faWarning = faWarning;
  faFileLines = faFileLines;
  faTrash = faTrash;
  reports = new MatTableDataSource<any>();
  displayedColumns: string[] = ['filler', 'name', 'reportId', 'reportStatus', 'reportAuth', 'baerTeam', 'action'];

  @Input() assessment: any;
  @Input() isEditable!: boolean;
  @Input() user: any;
  @Input() reportList: any;

  constructor(
    public dialog: MatDialog,
    private readonly reportService: ReportService,
    private readonly dialogService: DialogService,
    private readonly titleService: Title,
    public readonly toast: ConfirmationToastComponent,
  ) {
  }

  ngOnChanges(): void {
    this.loadData()
  }

  isDeletable(report: any) {
    return this.user.isWO && (report.report_status_id === 0 || !this.assessment.tentative_start_week || new Date() < new Date(this.assessment.tentative_start_week));
  }

  onDelete(report: any) {
    this.dialog.open(DeleteFireComponent, {
      maxWidth: "900px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
      data: {
        fireRecord: this.assessment,
        report,
      },
    }).afterClosed().subscribe((reload) => {
      if (reload) {
        this.toast.openSuccessSnackBar('', '', `<strong>${this.assessment.baer_name} (${report.report_num}) report</strong> was successfully deleted.`)
        this.reportList.splice(this.reportList.findIndex((r: any) => r.report_id === report.report_id), 1);
        this.loadData();
      }
    });
  }

  loadData() {
    let reports = this.reportList;
    if (reports) {
      reports.sort((reportA: any, reportB: any) => reportA.report_num - reportB.report_num)
      this.setEditors(reports);
      this.setTooltip(reports);
      this.reports.data = reports;
    }
  }

  setEditors(reports: any) {
    reports.forEach((report: any) => {
      let edit_ids = report.users.filter((user: any) => user.editor)?.map((x: any) => x.user_id) ?? [];
      edit_ids.push(report.assessment.team_lead_id);
      report.edit_ids = edit_ids;

      let actions = [];
      if (report.report_status_id != 2) { // SUBMITTED
        if (((this.user.isROCO?.length ?? 0) > 0) || (edit_ids.includes(this.user.user_id) || this.isEditable)) {
          actions.push("edit");
        }
      }
      else if (((this.user.isWOCO?.length >= 1) || ((this.user.isROCO?.length ?? 0) > 0)) && [0, 1, 2, 3].includes(report.authorization_status_id)) {
        actions.push("edit");
      }
      actions.push("view", "download");
      if (this.isDeletable(report)) {
        actions.push("delete");
      }
      report.actions = actions;



    })
  }

  setTooltip(reports: any) {
    reports.forEach((report: any) => {
      let tooltip = 'Team Lead(s)\n';
      for (let member of report.users.filter((member: any) => member.role_id === 0)) {
        if (member.user)
          tooltip += member.user.first_name + ' ' + member.user.last_name + '\n'
      }
      for (let member of report.users.filter((member: any) => member.role_id === 1)) {
        if (member.user)
          tooltip += member.user.first_name + ' ' + member.user.last_name + '\n'
      }
      tooltip += '\nTeam Member(s)\n'
      for (let member of report.users.filter((member: any) => member.role_id === 2)) {
        if (member.user)
          tooltip += member.user.first_name + ' ' + member.user.last_name + '\n'
      }
      for (let member of report.users.filter((member: any) => member.role_id === 3)) {
        if (member.user)
          tooltip += member.user.first_name + ' ' + member.user.last_name + '\n'
      }
      report.tooltip = tooltip;
      this.addSupportMessage(report);
    });
  }

  addSupportMessage(report: any) {
    if (report.supports?.length > 0) {
      let currentRequests = report.supports.filter((support: any) => {
        return !support.support_completed
      })
      if (currentRequests.length == 1) {
        let support = currentRequests[0]
        let skillsText = this.setSkillsText(support)
        let addDetailsText = ""
        if (support.additional_details) {
          addDetailsText = `\n\nAdditional Details:\n${support.additional_details}`
        }
        report.support_tooltip = `Interregional Support Request\n\nType of Support:\n${support.support_type} ${skillsText}${addDetailsText}`
      } else if (currentRequests.length > 1) {
        report.support_tooltip = `There are ${currentRequests.length} requests on this report. Visit the Teams tab to view the details.`
      }
    }
  }

  setSkillsText(support: any) {
    let skillsText = ""
    if (support.support_type == 'Team Member(s)' && support.support_skills != "") {
      let skills = support.support_skills.split(", ")
      if (skills.length == 1) {
        skillsText = support.support_skills
      } else if (skills.length == 2) {
        skillsText = skills.join(" and ")
      } else {
        skills[skills.length - 1] = "and " + skills[skills.length - 1]
        skillsText = skills.join(", ")
      }
      skillsText = "\n\nSkills:\n" + skillsText
    }
    return skillsText;
  }

  requestSupport(report: any): void {
    this.dialog.open(RequestSupportComponent, {
      maxWidth: "900px",
      panelClass: ['animate__animated', 'animate__fadeInDown'],
      data: {
        fireRecord: this.assessment,
        report: report,
      },
    }).afterClosed().subscribe((reload) => {
      this.titleService.setTitle("BAER - Assessments")
      if (reload) {
        this.loadData()
      }
    })
  }

  stopPropagation(event: any) {
    event.stopPropagation();
  }
}
